import { getOrderQuotationPublicId } from "./quotationCheckout";

/** Customer-facing labels from admin DB `status` + `payment_status` (logic unchanged). */

export const ORDER_WORK_STATUS = {
  PENDING: "0",
  EN_ROUTE: "1",
  DELIVERED: "2",
  CANCELLED: "3",
  IN_PROCESS: "4",
  PROCEED_WO: "5",
  DISPATCHED: "6",
} as const;

export const ORDER_PAYMENT_STATUS = {
  PENDING: "0",
  PAID: "1",
  REJECTED: "2",
  REFUNDED: "3",
  PROCESSING: "4",
  /** Wire transfer only — refund initiated, pending manual confirmation. */
  WT_REFUND_PENDING: "5",
} as const;

export const isWireTransfer = (paymentType: unknown): boolean =>
  Number(paymentType) === 1;

/**
 * Customer order status from admin work status.
 * En Route (status 1) → In Process.
 * Dispatched (status 6) → In Transit.
 */
export function getCustomerOrderStatusKey(row?: {
  status?: unknown;
  payment_type?: unknown;
} | null): "pending" | "in_process" | "in_transit" | "delivered" | "cancelled" {
  const s = String(row?.status ?? "");

  if (s === ORDER_WORK_STATUS.PENDING) return "pending";
  if (
    s === ORDER_WORK_STATUS.PROCEED_WO ||
    s === ORDER_WORK_STATUS.IN_PROCESS ||
    s === ORDER_WORK_STATUS.EN_ROUTE
  ) {
    return "in_process";
  }
  if (s === ORDER_WORK_STATUS.DISPATCHED) {
    return "in_transit";
  }
  if (s === ORDER_WORK_STATUS.DELIVERED) return "delivered";
  /* Admin Cancelled vs Cancelled (Requested) both use status=3; customer sees "Cancelled" only. */
  if (s === ORDER_WORK_STATUS.CANCELLED) return "cancelled";
  return "pending";
}

/** Customer payment status from admin payment_status + payment_type. */
export function getCustomerPaymentStatusKey(row?: {
  payment_status?: unknown;
  payment_type?: unknown;
} | null):
  | "pending"
  | "declined"
  | "wt_pending"
  | "paid_cc"
  | "paid_wt"
  | "refunded"
  | "rejected"
  | "processing"
  | "wt_refund_pending" {
  const ps = String(row?.payment_status ?? "");
  const wire = isWireTransfer(row?.payment_type);

  if (ps === ORDER_PAYMENT_STATUS.PENDING) {
    return wire ? "wt_pending" : "declined";
  }
  if (ps === ORDER_PAYMENT_STATUS.PAID) {
    return wire ? "paid_wt" : "paid_cc";
  }
  if (ps === ORDER_PAYMENT_STATUS.REFUNDED) return "refunded";
  if (ps === ORDER_PAYMENT_STATUS.REJECTED) return "rejected";
  if (ps === ORDER_PAYMENT_STATUS.PROCESSING) return "processing";
  if (ps === ORDER_PAYMENT_STATUS.WT_REFUND_PENDING) {
    return wire ? "wt_refund_pending" : "pending";
  }
  return wire ? "wt_pending" : "pending";
}

/** Customer-facing status text colors ("Customer sees" spec). */
export const CUSTOMER_ORDER_STATUS_COLORS = {
  green: "#4BA100",
  red: "#FF0000",
  yellow: "#E5B000",
  darkBlue: "#1565c0",
  cyan: "#00D0FF",
} as const;

export type CustomerPaymentStatusKey = ReturnType<
  typeof getCustomerPaymentStatusKey
>;
export type CustomerOrderStatusKey = ReturnType<typeof getCustomerOrderStatusKey>;

/** CSS class suffix for payment status color (use with `customer-order-status`). */
/** Online (card) payment declined — customer may retry checkout from order detail. */
export function canRetryOnlinePayment(row?: {
  payment_status?: unknown;
  payment_type?: unknown;
  status?: unknown;
  quotation_id?: unknown;
  quotation_detail?: { quotation_id?: unknown } | null;
} | null): boolean {
  if (!row) return false;
  if (getCustomerOrderStatusKey(row) === "cancelled") return false;
  if (getCustomerPaymentStatusKey(row) !== "declined") return false;
  return getOrderQuotationPublicId(row) != null;
}

export function getCustomerPaymentStatusColorClass(
  key: CustomerPaymentStatusKey,
): string {
  if (key === "wt_pending" || key === "declined" || key === "rejected") {
    return "customer-order-status--red";
  }
  if (key === "paid_cc" || key === "paid_wt" || key === "refunded") {
    return "customer-order-status--green";
  }
  if (key === "wt_refund_pending" || key === "pending" || key === "processing") {
    return "customer-order-status--yellow";
  }
  return "customer-order-status--red";
}

/** CSS class suffix for order/delivery status color (use with `customer-order-status`). */
export function getCustomerOrderStatusColorClass(
  key: CustomerOrderStatusKey,
): string {
  if (key === "pending") return "customer-order-status--yellow";
  if (key === "in_process") return "customer-order-status--dark-blue";
  if (key === "in_transit") return "customer-order-status--cyan";
  if (key === "delivered") return "customer-order-status--green";
  if (key === "cancelled") return "customer-order-status--red";
  return "customer-order-status--yellow";
}
